import { DateRange } from '@clickcease/cc-client-components-lib'
import { addSeconds, endOfDay, format, getUnixTime } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { Timezones } from 'Assets/lists/timezones'

export const MONTH_AND_A_HALF = 45
export const WEEK_DAYS = 7

export const DATE_FORMATS = {
  MONTH_DAY_FORMAT: 'MMM d',
  MONTH_DAY_FULL_YEAR: 'MMM dd, yyyy',
} as const

export const getFormattedDate = (date: Date) => {
  return format(date, DATE_FORMATS.MONTH_DAY_FULL_YEAR)
}
export const getFormattedDateByString = (date: string) => getFormattedDate(new Date(date))

export const isToday = (date: Date) => {
  return date.getDate() === new Date().getDate()
}

export const formatDate = (date = new Date()) => {
  const year = date.getFullYear()
  let month = (date.getMonth() + 1).toString()
  let day = date.getDate().toString()

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  return [year, month, day].join('-')
}

export const getPastDate = (day = 0, date: Date) => {
  return date.setDate(date.getDate() - day)
}
export const getPastFormattedDate = (day = 0) => {
  const date = new Date()
  date.setDate(date.getDate() - day)
  return formatDate(date)
}

export const weekAgo = () => {
  return new Date().getDate() - 7
}

export const today = (): string => {
  return getPastFormattedDate()
}

export const monthAgo = (): string => {
  return getPastFormattedDate(30)
}

export const getCustomDate = (timestamp: number): string => {
  const date = new Date(timestamp * 1000)
  date.setDate(date.getDate())
  return formatDate(date)
}

export const getDateRangeSelectTitle = (from: Date, to: Date) => {
  const formattedFrom = format(from, DATE_FORMATS.MONTH_DAY_FORMAT)
  const formattedTo = format(to, DATE_FORMATS.MONTH_DAY_FORMAT)
  const fromYear = from.getFullYear()
  const toYear = to.getFullYear()
  const difference = Math.abs(toYear - fromYear)
  const minYear = Math.min(fromYear, toYear)
  const formattedYear = difference ? `${minYear}/${(minYear % 10) + difference}` : minYear //Example: 2023 or 2022/3
  return `${formattedFrom} - ${formattedTo}, ${formattedYear}`
}

export const DEFAULT_TIMEZONE = 'UTC'

export const getCountryLabelByCountryValue = (countryValue: string) => {
  if (!countryValue) return DEFAULT_TIMEZONE
  if (!countryValue.includes('/')) return countryValue // case input countryValue is already a country label

  return Timezones.find((timezone) => timezone.value === countryValue)?.label || DEFAULT_TIMEZONE
}

export const getCountryValueByCountryLabel = (countryLabel: string) => {
  if (!countryLabel) return DEFAULT_TIMEZONE
  if (countryLabel.includes('/')) return countryLabel // case input countryLabel is already a country value

  return Timezones.find((timezone) => timezone.label === countryLabel)?.value || DEFAULT_TIMEZONE
}

export const toUnixDateRange: (
  dateRange?: DateRange
) => { from: number; to: number } | undefined = (dateRange) => {
  if (dateRange?.from && dateRange?.to)
    return {
      from: getUnixTime(dateRange.from),
      to: getUnixTime(dateRange.to),
    }
  return undefined
}

export const toUnixDateRangeFix = (timezone: string, dateRange: DateRange | undefined) => {
  if (!dateRange || !dateRange.from || !dateRange.to) {
    return undefined
  }

  const rangeNormalizedToUtcTemp = {
    from: getUnixTime(zonedTimeToUtc(addSeconds(dateRange.from, -1), timezone)),
    to: getUnixTime(zonedTimeToUtc(endOfDay(dateRange.to), timezone)),
  }

  return rangeNormalizedToUtcTemp
}

export const unixToDate = (date: number) => new Date(date * 1000)

export const trailDaysLeft = (date: number | string) => {
  if (!date) return null

  const today = new Date()
  const trailDate = new Date(date)
  const timeDiff = Math.abs(trailDate.getTime() - today.getTime())
  const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24))
  return diffDays
}

export const toUnixDateRangeNew = (timezone: string, dateRange: DateRange | undefined) => {
  if (!dateRange || !dateRange.from || !dateRange.to) {
    return undefined
  }

  const rangeNormalizedToUtcTemp = {
    from: getUnixTime(
      zonedTimeToUtc(addSeconds(dateRange.from, -1), getCountryValueByCountryLabel(timezone))
    ),
    to: getUnixTime(
      zonedTimeToUtc(endOfDay(dateRange.to), getCountryValueByCountryLabel(timezone))
    ),
  }

  return rangeNormalizedToUtcTemp
}
